import { useState, FC } from 'react';

import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';

import { colors } from 'styles';
import { ICarouselItem } from 'types/applications';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  ImageWrapper,
  TextWrapper,
  CarouselContent,
  DotIndicator,
  AuthorInfoWrapper,
} from './Carousel.styles';
import { SliderArrow } from 'components/SliderArrow';

interface CarouselProps {
  items: ICarouselItem[];
}

const Carousel: FC<CarouselProps> = ({ items }) => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const minSwipeDistance = 50;

  const handleTransition = (newIndex: number) => {
    if (isTransitioning || newIndex === current) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setCurrent(newIndex);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 200);
  };

  const onPrevHandler = () => {
    const newIndex = current === 0 ? items.length - 1 : current - 1;
    handleTransition(newIndex);
  };

  const onNextHandler = () => {
    const newIndex = current === items.length - 1 ? 0 : current + 1;
    handleTransition(newIndex);
  };

  const onClickHandler = (index: number) => {
    handleTransition(index);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      onNextHandler();
    } else if (isRightSwipe) {
      onPrevHandler();
    }
  };

  const { authorName, authorRole, text, authorImage } = items[current];

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      width="100%"
      padding="112px 0"
      onTouchStart={isMobile ? onTouchStart : undefined}
      onTouchMove={isMobile ? onTouchMove : undefined}
      onTouchEnd={isMobile ? onTouchEnd : undefined}
    >
      {items.length > 1 && !isMobile && (
        <>
          <SliderArrow
            onClick={onPrevHandler}
            sx={{
              display: { xs: 'none', sm: 'block' },
            }}
          />
          <SliderArrow
            onClick={onNextHandler}
            sx={{
              right: 64,
              left: 'unset',
              transform: 'rotate(180deg)',
              display: { xs: 'none', sm: 'block' },
            }}
          />
        </>
      )}

      <CarouselContent className={isTransitioning ? 'fade-enter' : ''}>
        <TextWrapper
          sx={{
            opacity: isTransitioning ? 0 : 1,
            transform: isTransitioning ? 'translateY(20px)' : 'translateY(0)',
          }}
        >
          {text}
        </TextWrapper>

        <Box
          margin="32px 0 16px"
          sx={{
            opacity: isTransitioning ? 0 : 1,
            transform: isTransitioning ? 'scale(0.9)' : 'scale(1)',
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <ImageWrapper>
            <img src={authorImage} alt="avatar images" />
          </ImageWrapper>
        </Box>

        <AuthorInfoWrapper
          sx={{
            opacity: isTransitioning ? 0 : 1,
            transform: isTransitioning ? 'translateY(10px)' : 'translateY(0)',
            transition: 'all 0.3s ease-in-out 0.1s',
          }}
        >
          <Typography
            fontSize="16px"
            fontWeight={600}
            lineHeight="150%"
            color={colors.mainDark}
            textAlign="center"
          >
            {authorName}
          </Typography>

          <Typography fontSize="16px" lineHeight="150%" color={colors.mainDark} textAlign="center">
            {authorRole}
          </Typography>
        </AuthorInfoWrapper>

        {items.length > 1 && (
          <Box
            marginTop="48px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              backgroundColor: colors.brandLightGrey,
              height: '32px',
              borderRadius: '16px',
              padding: '16px',
            }}
          >
            {items.map((item, idx) => (
              <DotIndicator
                key={item.authorName}
                isActive={idx === current}
                onClick={() => onClickHandler(idx)}
                sx={{
                  marginLeft: idx === 0 ? 0 : '9px',
                  pointerEvents: isTransitioning ? 'none' : 'auto',
                }}
              />
            ))}
          </Box>
        )}
      </CarouselContent>
    </Box>
  );
};

export default Carousel;
